import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import L from 'leaflet';
import { ENDPOINTS } from '../config/api';
import 'leaflet/dist/leaflet.css';
import '../styles/Estaciones.css';

const Estaciones = () => {
  const [estaciones, setEstaciones] = useState([]); 
  const [loading, setLoading] = useState(true); 
  const [error, setError] = useState(null); 
  const [searchParams, setSearchParams] = useSearchParams(); 
  const [estacionResaltada, setEstacionResaltada] = useState(null); // Estado para controlar estación resaltada
  const [mostrarBotonRecargar, setMostrarBotonRecargar] = useState(false); // Estado para controlar si mostrar el botón de recargar
  const hasFetchedRef = React.useRef(false); // Referencia mutable que persiste entre renders
  const mapRef = useRef(null); // Referencia al mapa para poder acceder desde el botón
  
  // Obtener el parámetro de la estación desde la URL
  const estacionSeleccionada = searchParams.get('estacion'); 

  // Sincronizar estado inicial cuando se navega desde Instrumentacion.js
  useEffect(() => {
    if (estacionSeleccionada && !estacionResaltada) {
      setEstacionResaltada(estacionSeleccionada); 
      
      // Scroll automático al mapa cuando se navega desde Instrumentacion.js
      setTimeout(() => {
        const mapElement = document.getElementById('map'); 
        if (mapElement) {
          mapElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'center'
          }); 
        }
      }, 200); // Pequeño delay para asegurar que el DOM esté listo
    }
  }, [estacionSeleccionada]); // Solo depende de estacionSeleccionada, NO de estacionResaltada

  // Función específica para el botón de reset
  const handleResetButton = () => {
    
    // Resetear el estado local inmediatamente
    setEstacionResaltada(null); 
    setMostrarBotonRecargar(false); // No mostrar el botón de recargar
    
    
    // Quitar el parámetro 'estacion' de la URL
    const newSearchParams = new URLSearchParams(searchParams); 
    newSearchParams.delete('estacion'); 
    setSearchParams(newSearchParams); 
    
    // Resetear el marcador en el mapa si existe
    if (mapRef.current && mapRef.current.highlightedMarkerRef) {
      
      // Obtener la estación que estaba resaltada
      const estacionParaResetear = estaciones.find(est => 
        est.nombre === mapRef.current.highlightedStationName || est.estacion === mapRef.current.highlightedStationName
      ); 
      
      if (estacionParaResetear) {
        // Crear icono normal
        const normalIcon = L.icon({
          iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
          iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
          // Eliminar sombra
          iconSize: [25, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34]
        }); 
        
        // Remover marcador resaltado
        mapRef.current.removeLayer(mapRef.current.highlightedMarkerRef); 
        
        // Crear nuevo marcador normal
        const normalMarker = L.marker([estacionParaResetear.latitud, estacionParaResetear.longuitud, estacionParaResetear.elevacion], { icon: normalIcon })
          .addTo(mapRef.current)
          .bindPopup(`<strong>${estacionParaResetear.nombre}</strong><br>Lat: ${estacionParaResetear.latitud}<br>Lon: ${estacionParaResetear.longuitud}<br>Elev: ${estacionParaResetear.elevacion || 'N/A'} m`); 
        
        // Limpiar referencias del mapa
        mapRef.current.highlightedMarkerRef = null;
        mapRef.current.highlightedStationName = null;
      }
    }
    
    // Enfocar el mapa para mostrar todas las estaciones
    if (mapRef.current && estaciones.length > 0) {
      
      // Calcular los límites de todas las estaciones
      const latitudes = estaciones.map(est => est.latitud); 
      const longitudes = estaciones.map(est => est.longuitud); 
      
      const bounds = [
        [Math.min(...latitudes), Math.min(...longitudes)], // Suroeste
        [Math.max(...latitudes), Math.max(...longitudes)]  // Noreste
      ];
      
      // Ajustar el mapa para mostrar todos los marcadores con un poco de padding
      mapRef.current.fitBounds(bounds, {
        padding: [20, 20], // Padding en píxeles
        maxZoom: 10 // Zoom máximo para evitar acercarse demasiado
      }); 
    }
  };

  // Función para resaltar un marcador
  const highlightMarker = (map, estacion, markerToRemove) => {
    // Crear icono SVG personalizado con color #17a2b8
    const highlightedIcon = L.divIcon({
      html: `
        <svg width="35" height="55" viewBox="0 0 25 41" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 0C5.383 0 0 5.383 0 12c0 10.5 12 29 12 29s12-18.5 12-29c0-6.617-5.383-12-12-12z" 
                fill="#17a2b8" stroke="#ffffff" stroke-width="2"/>
          <circle cx="12" cy="12" r="6" fill="#ffffff"/>
        </svg>
      `,
      className: 'estacion-highlighted-marker',
      iconSize: [35, 55],
      iconAnchor: [17, 55],
      popupAnchor: [1, -34]
    }); 
    
    // Si hay un marcador para remover, removerlo
    if (markerToRemove) {
      map.removeLayer(markerToRemove); 
    }
    
    // Crear marcador resaltado
    const highlightedMarker = L.marker([estacion.latitud, estacion.longuitud, estacion.elevacion], { icon: highlightedIcon })
      .addTo(map)
      .bindPopup(`<strong>${estacion.nombre}</strong><br>Lat: ${estacion.latitud}<br>Lon: ${estacion.longuitud}<br>Elev: ${estacion.elevacion || 'N/A'} m`); 
    
    // Centrar el mapa en la estación con transición suave
    map.flyTo([estacion.latitud, estacion.longuitud], 12, {
      duration: 1.0, // Duración de la animación en segundos
      easeLinearity: 0.25 // Suavidad de la transición
    }); 
    
    // Abrir el popup automáticamente después de la transición
    setTimeout(() => {
      highlightedMarker.openPopup(); 
    }, 5000); // Esperar 1 segundo para que termine la transición
    
    // Guardar referencia al marcador resaltado
    map.highlightedMarkerRef = highlightedMarker;
    map.highlightedStationName = estacion.nombre;
    
    // Actualizar estado para el botón
    setEstacionResaltada(estacion.nombre); 
    
    if (process.env.NODE_ENV === 'development') { console.log(`Estación resaltada: ${estacion.nombre}`)}; 
  };

  // Función para resetear un marcador resaltado
  const resetHighlightedMarker = (map) => {
    if (map.highlightedMarkerRef && map.highlightedStationName) {
      
      // Obtener la estación que estaba resaltada
      const estacionResaltada = estaciones.find(est => 
        est.nombre === map.highlightedStationName || est.estacion === map.highlightedStationName
      ); 
      
      if (estacionResaltada) {
        // Crear icono normal
        const normalIcon = L.icon({
          iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
          iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
          // Eliminar sombra
          iconSize: [25, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34]
        }); 
        
        // Remover marcador resaltado
        map.removeLayer(map.highlightedMarkerRef); 
        
        if (process.env.NODE_ENV === 'development') { console.log('📍 Creando nuevo marcador normal...')}; 
        // Crear nuevo marcador normal en la misma posición
        const normalMarker = L.marker([estacionResaltada.latitud, estacionResaltada.longuitud, estacionResaltada.elevacion], { icon: normalIcon })
          .addTo(map)
          .bindPopup(`<strong>${estacionResaltada.nombre}</strong><br>Lat: ${estacionResaltada.latitud}<br>Lon: ${estacionResaltada.longuitud}<br>Elev: ${estacionResaltada.elevacion || 'N/A'} m`); 
        // Limpiar referencias
        map.highlightedMarkerRef = null;
        map.highlightedStationName = null;
        
        // Actualizar estado para el botón
        setEstacionResaltada(null); 
      } else {
        if (process.env.NODE_ENV === 'development') { console.log('No se encontró la estación para resetear')}; 
      }
    } else {
      if (process.env.NODE_ENV === 'development') { console.log('highlightedMarkerRef existe:', !!map.highlightedMarkerRef) };
      if (process.env.NODE_ENV === 'development') { console.log('highlightedStationName existe:', !!map.highlightedStationName) };
    }
  };



  useEffect(() => {
    // Pequeño delay para asegurar que el DOM esté listo
    const timer = setTimeout(() => {
      // Verificar que el elemento del mapa esté disponible
      const mapElement = document.getElementById('map'); 
      if (!mapElement) {
        return;
      }
      
      // Crear el mapa solo si no existe
      let map;
      try {
        map = L.map('map', {
          zoomControl: false, // Deshabilitar controles de zoom
          scrollWheelZoom: true, // Deshabilitar zoom con rueda del mouse
          doubleClickZoom: false, // Deshabilitar zoom con doble clic
          boxZoom: false, // Deshabilitar zoom con caja
          keyboard: false // Deshabilitar zoom con teclado
        }).setView([32.2, -115.5], 7); 
        
        mapRef.current = map; // Guardar referencia al mapa
      } catch (error) {
        if (process.env.NODE_ENV === 'development') { console.error('Error creando mapa:', error) };
        return;
      }

      // Agregar tile layer
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors'
      }).addTo(map); 

      // Event listener para resetear el icono resaltado cuando se hace clic en el mapa
      map.on('click', function(e) {
        // Solo resetear si hay un marcador resaltado
        if (map.highlightedMarkerRef && map.highlightedStationName) {
          resetHighlightedMarker(map); 
        } else {
          if (process.env.NODE_ENV === 'development') { console.log('No hay marcador resaltado para resetear') };
        }
      }); 

      // Crear icono personalizado sin sombra
      const customIcon = L.icon({
        iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
        iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
        // Eliminar sombra para evitar problemas de z-index
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34]
      }); 

      // Función para obtener estaciones y agregar marcadores
      const initializeStations = async () => {
        if (hasFetchedRef.current) {
          return;
        }

        try {
          setLoading(true); 
          setError(null); 
          
          const response = await fetch(ENDPOINTS.estaciones); 
          if (!response.ok) {
            throw new Error(`Error: ${response.status}`); 
          }
          
          const data = await response.json(); 
          setEstaciones(data); 
          hasFetchedRef.current = true;
          
                  // Agregar marcadores al mapa
        if (data && data.length > 0) {
          data.forEach(est => {
            // Crear marcador con icono personalizado
            const marker = L.marker([est.latitud, est.longuitud], { icon: customIcon })
              .addTo(map)
              .bindPopup(`<strong>${est.nombre}</strong><br>Lat: ${est.latitud}<br>Lon: ${est.longuitud}<br>Elev: ${est.elevacion || 'N/A'} m`); 
            
            // Event listener para clic en marcador individual
            marker.on('click', function(e) {
              // Solo abrir el popup, NO resaltar el marcador
              // El resaltado solo debe ocurrir cuando se navega desde Instrumentacion.js
              if (process.env.NODE_ENV === 'development') { console.log(`Clic en marcador: ${est.nombre}`) };
            }); 
            
            // Si esta es la estación seleccionada desde Instrumentacion.js, resaltarla
            if (estacionSeleccionada && (est.nombre === estacionSeleccionada || est.estacion === estacionSeleccionada)) {
              // Remover marcador normal y crear uno resaltado
              map.removeLayer(marker); 
              highlightMarker(map, est, null); 
              
              // Enfocar el mapa en la estación seleccionada con una transición suave
              map.flyTo([est.latitud, est.longuitud], 12, {
                duration: 1.5, // Duración de la animación en segundos
                easeLinearity: 0.25 // Suavidad de la transición
              }); 
              
              // Scroll adicional después de la animación del mapa
              setTimeout(() => {
                const mapElement = document.getElementById('map'); 
                if (mapElement) {
                  mapElement.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'center',
                    inline: 'center'
                  }); 
                  if (process.env.NODE_ENV === 'development') { console.log('✅ Scroll adicional después de flyTo ejecutado') };
                }
              }, 2000); // Esperar 2 segundos (1.5s de flyTo + 0.5s extra)
              
              if (process.env.NODE_ENV === 'development') { console.log(`Estación resaltada desde navegación: ${est.nombre}`) };
            }
          }); 
          if (process.env.NODE_ENV === 'development') { console.log('Marcadores agregados al mapa') };
        }
          
        } catch (err) {
          if (process.env.NODE_ENV === 'development') { console.error('Error:', err) };
          setError(err.message); 
          setEstaciones([]); 
        } finally {
          setLoading(false); 
        }
      };

      // Inicializar estaciones
      initializeStations(); 

    }, 100); // 100ms de delay

    // Cleanup
    return () => {
      clearTimeout(timer); 
      if (process.env.NODE_ENV === 'development') { console.log('Cleanup ejecutándose') };
    };
  }, []); 

  return (
    <div className="presentation-page pt-16">
      {/* Hero Section */}
      <header className="estaciones-hero-section">
        <div className="estaciones-hero-content">
          <div className="estaciones-hero-container">
            {/* Hero Card */}
            <div className="estaciones-hero-card">
              <div className="estaciones-hero-grid">
                <div className="estaciones-hero-left">
                  <h1 className="estaciones-hero-title">
                    <span className="estaciones-hero-title-gradient">Mapa de</span> Estaciones de 
                    <span className="estaciones-hero-title-gradient"> RESNOM</span>
                  </h1>
                </div>
                <div className="estaciones-hero-right">
                  <p className="estaciones-hero-description">
                    Explora la ubicación geográfica de todas las estaciones de la Red Sísmica del Noroeste de México. Visualiza en tiempo real la distribución de nuestros sensores sísmicos y accede a información detallada de cada estación.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mapa de Estaciones Section */}
      <section className="estaciones-map-section">
        <div className="estaciones-map-container">
            {/* Contenedor del mapa */}
            <div className="estaciones-map-wrapper">
              {/* Botón de acción dinámico */}
              <div className="estaciones-action-buttons">
                {estacionResaltada ? (
                  <button
                    onClick={handleResetButton}
                    className="estaciones-reset-button"
                  >
                    <i className="fas fa-times-circle"></i>
                    Quitar Resaltado de {estacionResaltada}
                  </button>
                ) : error ? (
                  <button
                    onClick={() => {
                      if (process.env.NODE_ENV === 'development') { console.log('Recargando estaciones...') };
                      window.location.reload(); 
                    }}
                    className="estaciones-reload-button"
                    disabled={loading}
                  >
                    {loading ? 'Cargando...' : 'Recargar Estaciones'}
                  </button>
                ) : null}
              </div>
              
              {/* Indicador de estado */}
              {loading && (
                <div className="estaciones-loading">
                  <div className="estaciones-loading-spinner"></div>
                  <p>Cargando estaciones desde la API...</p>
                </div>
              )}
              

              
              <div id="map" className="estaciones-map"></div>
              
              {/* Información de estado - Solo cuando hay problemas */}
              {!loading && error && (
                <div className="estaciones-error">
                  <i className="fas fa-exclamation-triangle"></i>
                  <p>Error: {error}</p>
                  <p className="estaciones-error-note">No se pudieron cargar las estaciones desde la API</p>
                </div>
              )}
              
              {!loading && !error && estaciones.length === 0 && (
                <div className="estaciones-no-data">
                  <i className="fas fa-info-circle"></i>
                  <p>No hay estaciones disponibles en este momento</p>
                </div>
              )}
              
              {/* Mensaje cuando hay una estación resaltada */}
              {!loading && !error && estaciones.length > 0 && estacionResaltada && (
                <div className="estaciones-highlighted">
                  <i className="fas fa-map-marker-alt"></i>
                  <p>Estación resaltada: <strong>{estacionResaltada}</strong></p>
                  <p className="estaciones-highlighted-note">Navegaste desde la tabla de instrumentación</p>
                </div>
              )}
            </div>
          
          {/* Información adicional */}
          <div className="estaciones-info">
            <div className="estaciones-info-grid">
              <div className="estaciones-info-card">
                <div className="estaciones-info-icon">
                  <i className="fas fa-map-marker-alt"></i>
                </div>
                <h3 className="estaciones-info-title">Cobertura Regional</h3>
                <p className="estaciones-info-description">
                  La red cubre el noroeste de México incluyendo Baja California, Sonora y áreas adyacentes, 
                  proporcionando monitoreo sísmico integral de la región.
                </p>
              </div>
              
              <div className="estaciones-info-card">
                <div className="estaciones-info-icon">
                  <i className="fas fa-satellite-dish"></i>
                </div>
                <h3 className="estaciones-info-title">Transmisión en Tiempo Real</h3>
                <p className="estaciones-info-description">
                  Todas las estaciones transmiten datos sísmicos en tiempo real al centro de datos de RESNOM 
                  para análisis y procesamiento inmediato.
                </p>
              </div>
              
              <div className="estaciones-info-card">
                <div className="estaciones-info-icon">
                  <i className="fas fa-chart-line"></i>
                </div>
                <h3 className="estaciones-info-title">Monitoreo Continuo</h3>
                <p className="estaciones-info-description">
                  Las estaciones operan las 24 horas del día, los 365 días del año, 
                  garantizando la detección continua de actividad sísmica en la región.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  ); 
};

export default Estaciones; 